// # State management with Zustand
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { db } from "./firebase";
import { doc, getDoc, setDoc } from "firebase/firestore"; // # Import Firestore methods
import sessionManager from "./session";
import { auth } from "./firebase";
// # Import verification functions
import {
  syncVerificationStatus,
  sendVerificationEmail,
} from "./verify/verify_user_email";

// # Create auth store with persistence
const useAuthStore = create(
  persist(
    (set, get) => ({
      // # Initial state
      user: null,
      isAuthenticated: false,
      loading: true,
      userProfile: null,
      userDashboard: null,
      isProfileComplete: false,
      isAdmin: false,
      isTutor: false,
      isContentReviewer: false,
      isFinancialManager: false,
      isDesigner: false,
      isSupport: false,
      userRole: "user",
      signedOut: false,
      deviceLimitReached: false,
      // # Add email verification status
      emailVerified: false,

      // # Set loading state
      setLoading: (loading) => set({ loading }),

      // # Initialize user session with force refresh option
      initializeUserSession: async (
        user,
        forceRefresh = false,
        roleInfo = null
      ) => {
        try {
          set({ loading: true });

          // # Initialize session without subscription checks
          const session = await sessionManager.initializeSession(
            user,
            forceRefresh
          );

          if (!session) {
            throw new Error("Failed to initialize session");
          }

          // # Get user profile from Firestore
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);

          // # Create user profile if it doesn't exist
          if (!userSnap.exists()) {
            const timestamp = new Date().toISOString();
            const newUserProfile = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || "",
              photoURL: user.photoURL || "",
              isProfileComplete: false,
              role: roleInfo?.role || "user",
              emailVerified: user.emailVerified || false, // # Initialize email verification status
              metadata: {
                createdAt: timestamp,
                updatedAt: timestamp,
                lastLogin: timestamp,
              },
            };

            await setDoc(userRef, newUserProfile);
            set({ userProfile: newUserProfile });
          } else {
            set({ userProfile: userSnap.data() });
          }

          // # Get user dashboard
          const dashboardRef = doc(db, "user_dashboards", user.uid);
          const dashboardSnap = await getDoc(dashboardRef);

          if (dashboardSnap.exists()) {
            set({ userDashboard: dashboardSnap.data() });
          }

          // # Set role information from token claims if provided
          if (roleInfo) {
            sessionManager.setAdminStatus(roleInfo.isAdmin);
            sessionManager.setUserRole(roleInfo.role);

            set({
              isAdmin: roleInfo.isAdmin,
              isTutor: roleInfo.isTutor,
              isContentReviewer: roleInfo.isContentReviewer,
              isFinancialManager: roleInfo.isFinancialManager,
              isDesigner: roleInfo.isDesigner,
              isSupport: roleInfo.isSupport,
              userRole: roleInfo.role,
            });
          } else {
            // # Fallback to getting role from user profile
            const userProfile = userSnap.data();
            const role = userProfile?.role || "user";

            set({
              isAdmin: role === "admin",
              isTutor: role === "tutor",
              isContentReviewer: role === "content_reviewer",
              isFinancialManager: role === "financial_manager",
              isDesigner: role === "designer",
              isSupport: role === "support",
              userRole: role,
            });
          }

          // # Set authentication state first (non-blocking)
          set({
            user,
            isAuthenticated: true,
            isProfileComplete: userSnap.exists()
              ? userSnap.data().isProfileComplete
              : false,
            loading: false,
            signedOut: false,
            deviceLimitReached: session.deviceLimitReached || false,
            emailVerified: user.emailVerified, // # Use user's emailVerified as initial value
          });

          // # Sync email verification status in background (non-blocking)
          syncVerificationStatus(user.uid)
            .then((verificationStatus) => {
              if (verificationStatus.success) {
                set({ emailVerified: verificationStatus.isVerified });
              }
            })
            .catch((error) => {
              console.error(
                "Error syncing verification status in background:",
                error
              );
              // # Keep using user.emailVerified if sync fails
            });

          return true;
        } catch (error) {
          console.error("Error initializing user session:", error);
          set({
            loading: false,
            isAuthenticated: false,
            user: null,
            userProfile: null,
            userDashboard: null,
            isProfileComplete: false,
            isAdmin: false,
            isTutor: false,
            isContentReviewer: false,
            isFinancialManager: false,
            isDesigner: false,
            isSupport: false,
            userRole: "user",
            signedOut: true,
            deviceLimitReached: false,
            emailVerified: false,
          });
          return false;
        }
      },

      // # Set user
      setUser: (user) => {
        if (user) {
          set({
            user,
            isAuthenticated: true,
            signedOut: false,
            emailVerified: user.emailVerified,
          });
        } else {
          set({
            user: null,
            isAuthenticated: false,
            signedOut: true,
            emailVerified: false,
          });
        }
      },

      // # Set user role
      setUserRole: (role) => {
        set({
          userRole: role,
          isAdmin: role === "admin",
          isTutor: role === "tutor",
          isContentReviewer: role === "content_reviewer",
          isFinancialManager: role === "financial_manager",
          isDesigner: role === "designer",
          isSupport: role === "support",
        });

        // # Update session manager
        sessionManager.setUserRole(role);
      },

      // # Reset auth state
      resetAuth: () => {
        set({
          user: null,
          isAuthenticated: false,
          userProfile: null,
          userDashboard: null,
          isProfileComplete: false,
          isAdmin: false,
          isTutor: false,
          isContentReviewer: false,
          isFinancialManager: false,
          isDesigner: false,
          isSupport: false,
          userRole: "user",
          signedOut: true,
          deviceLimitReached: false,
        });
      },

      // # Set device limit reached
      setDeviceLimitReached: (reached) => set({ deviceLimitReached: reached }),

      // # Refresh user claims manually
      refreshUserClaims: async () => {
        const { initializeUserSession } = get();
        if (auth.currentUser) {
          if (window.refreshUserClaims) {
            return await window.refreshUserClaims();
          }
          return { success: false, message: "Refresh function not available" };
        }
        return { success: false, message: "No user logged in" };
      },

      // # Set email verification status
      setEmailVerified: (status) => {
        set({ emailVerified: status });
      },

      // # Send verification email
      sendVerificationEmail: async () => {
        const result = await sendVerificationEmail();
        return result;
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        signedOut: state.signedOut,
        deviceLimitReached: state.deviceLimitReached,
      }),
    }
  )
);

// # Create dashboard store
const useDashboardStore = create((set) => ({
  // # Dashboard state
  settings: {
    theme: "light",
    notifications: true,
  },
  stats: {
    bookmarksCount: 0,
    notesCount: 0,
    lastBookmark: null,
    lastNote: null,
  },
  quickAccess: {
    recentlyViewed: [],
    favorites: [],
  },

  // # Actions
  updateSettings: (newSettings) =>
    set((state) => ({
      settings: { ...state.settings, ...newSettings },
    })),

  updateStats: (newStats) =>
    set((state) => ({
      stats: { ...state.stats, ...newStats },
    })),

  addToRecentlyViewed: (item) =>
    set((state) => ({
      quickAccess: {
        ...state.quickAccess,
        recentlyViewed: [
          item,
          ...state.quickAccess.recentlyViewed.filter((i) => i.id !== item.id),
        ].slice(0, 5), // # Keep only last 5 items
      },
    })),

  toggleFavorite: (item) =>
    set((state) => ({
      quickAccess: {
        ...state.quickAccess,
        favorites: state.quickAccess.favorites.some((i) => i.id === item.id)
          ? state.quickAccess.favorites.filter((i) => i.id !== item.id)
          : [...state.quickAccess.favorites, item],
      },
    })),
}));

export { useAuthStore, useDashboardStore };
